import { computed, readonly, ref } from 'vue';

export type HexTimingPerfStage =
  | 'queue_to_fetch'
  | 'fetch_complete'
  | 'worker_decode_complete'
  | 'merge_complete'
  | 'first_accurate_paint';

export interface HexTimingPerfSample {
  stage: HexTimingPerfStage;
  ms: number;
  shardKey?: string;
  at: number;
}

const HEX_TIMING_PERF_ENABLED =
  import.meta.env.DEV && import.meta.env.VITE_HEX_TIMING_PERF !== '0';
const MAX_SAMPLES = 300;

const samples = ref<HexTimingPerfSample[]>([]);

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function recordSample(
  stage: HexTimingPerfStage,
  ms: number,
  shardKey?: string,
): void {
  if (!HEX_TIMING_PERF_ENABLED) return;
  const entry: HexTimingPerfSample = {
    stage,
    ms: Number.isFinite(ms) ? ms : 0,
    shardKey,
    at: nowMs(),
  };
  const next = [...samples.value, entry];
  if (next.length > MAX_SAMPLES) {
    next.splice(0, next.length - MAX_SAMPLES);
  }
  samples.value = next;
  const keyPart = shardKey ? ` (${shardKey})` : '';
  console.debug(`[hex-perf] ${stage}: ${entry.ms.toFixed(1)}ms${keyPart}`);
}

const averages = computed(() => {
  const bucket = new Map<HexTimingPerfStage, { sum: number; count: number }>();
  for (const sample of samples.value) {
    const row = bucket.get(sample.stage) ?? { sum: 0, count: 0 };
    row.sum += sample.ms;
    row.count += 1;
    bucket.set(sample.stage, row);
  }
  const result: Partial<Record<HexTimingPerfStage, number>> = {};
  for (const [stage, row] of bucket.entries()) {
    result[stage] = row.count > 0 ? row.sum / row.count : 0;
  }
  return result;
});

export function useHexTimingPerf() {
  return {
    enabled: HEX_TIMING_PERF_ENABLED,
    samples: readonly(samples),
    averages: readonly(averages),
    recordSample,
  };
}
